import { HttpClient } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { AuthorizeService } from 'src/api-authorization/authorize.service';

interface MediaItem {
  id: string;
  baseUrl: string;
  mimeType: string;
  filename: string;
  description?: string;
  creationTime?: string;
  createTime?: string;
  type?: string;
  width?: string;
  height?: string;
  productUrl?: string;
  cameraMake?: string;
  cameraModel?: string;
  focalLength?: string;
  apertureFNumber?: string;
  isoEquivalent?: string;
  exposureTime?: string;
}

interface PickerSessionResponse {
  sessionId: string;
  pickerUri: string;
  message?: string;
}

interface PickerSessionStatus {
  id: string;
  //isComplete: boolean;
  mediaItemsSet: boolean;
  //selectedMediaItemIds: string[];
   expireTime: string;
}

@Component({
	selector: 'photoframe-albums',
	templateUrl: './albums.component.html',
	styleUrls: ['albums.component.scss']
})
export class PhotoFrameAlbumsComponent implements OnInit, OnDestroy {
	public selectedMediaItems: MediaItem[] = [];
	public loading: boolean = false;
	public loadError: string = '';
	public pickerUri: string = '';
	public sessionId: string = '';
	public pollingInterval: any;
	public showPickerButton: boolean = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private http: HttpClient,
		private sanitizer: DomSanitizer,
		private authSvc: AuthorizeService,
		private renderer: Renderer2,
		@Inject(DOCUMENT) private document: Document) {

		const elm = this.document.getElementById("nav-menu");
		if (elm) this.renderer.addClass(elm, 'hideme');
		const elm2 = this.document.getElementById("app-footer");
		if (elm2) this.renderer.addClass(elm2, 'hideme');
	}

	public ngOnDestroy(): void {
		const elm = this.document.getElementById("nav-menu");
		if (elm) this.renderer.removeClass(elm, 'hideme');
		const elm2 = this.document.getElementById("app-footer");
		if (elm2) this.renderer.removeClass(elm2, 'hideme');
		
		// Clear polling interval
		if (this.pollingInterval) {
			clearInterval(this.pollingInterval);
		}
	}

	async ngOnInit(): Promise<void> {
		// Check if we're coming back from picker (check for sessionId in route)
		const sessionIdFromRoute = this.route.snapshot.queryParams['sessionId'];
		if (sessionIdFromRoute) {
			this.sessionId = sessionIdFromRoute;
			this.startPolling(sessionIdFromRoute);
		} else {
			this.createPickerSession();
		}
	}

	private createPickerSession(): void {
		this.loading = true;
		this.loadError = '';
		
		this.http.get('/api/photoframe/createPickerSession').subscribe(
			(resp: PickerSessionResponse) => {
				console.log('Picker session response:', resp);
				this.pickerUri = resp.pickerUri || '';
				this.sessionId = resp.sessionId || 'NOT_IN_RESPONSE?';
				this.showPickerButton = true;
				this.loading = false;

			},
			(err: any) => {
				const errorMessage = err?.error || 'Unable to create picker session';
				
				// Check if the error is due to missing access token
				if (typeof errorMessage === 'string' && errorMessage.includes('Access token not found in session')) {
					this.router.navigate(['/photoframe/login']);
					return;
				}
				
				this.loadError = errorMessage;
				this.loading = false;
			}
		);
	}

	public openPicker(): void {
		if (this.pickerUri && this.sessionId) {
			// Append /autoclose to the picker URI for web-based applications
			// This will automatically close the Google Photos window after selection
			const pickerUriWithAutoclose = this.pickerUri.endsWith('/autoclose') 
				? this.pickerUri 
				: this.pickerUri + '/autoclose';
			// Open in a new tab so the current page stays open
			window.open(pickerUriWithAutoclose, '_blank');
			// Start polling in this tab so we can detect when selection is complete
			// even if the user doesn't switch to the new tab
			this.startPolling(this.sessionId);
		}
	}

	private startPolling(sessionId: string): void {
		this.loading = true;
		this.sessionId = sessionId;

		// Poll every 2 seconds
		this.pollingInterval = setInterval(() => {
			this.checkSessionStatus(sessionId);
		}, 2000);

		// Initial check
		this.checkSessionStatus(sessionId);
	}

	private checkSessionStatus(sessionId: string): void {
		this.http.get(`/api/photoframe/picker/status/${sessionId}`).subscribe(
			(status: PickerSessionStatus) => {
				if (status.mediaItemsSet) { // && status.selectedMediaItemIds && status.selectedMediaItemIds.length > 0) {
					clearInterval(this.pollingInterval);
					this.loadSelectedMediaItems(sessionId);
					console.log("-------------------------------- MEDIA ITEMS SET - - - - DONE");
					//this.loadSelectedMediaItems(status.selectedMediaItemIds);
				} else if (!status.mediaItemsSet) {
					// Still waiting
					this.loading = true;
				}
			},
			(err: any) => {
				console.error('Error checking session status:', err);
				this.loadError = 'Error checking selection status';
				this.loading = false;
				if (this.pollingInterval) {
					clearInterval(this.pollingInterval);
				}
			}
		);
	}

	private loadSelectedMediaItems(sessionId:string): void {
		this.http.get(`/api/photoframe/picker/mediaItems/${sessionId}`).subscribe(
			(items: MediaItem[]) => {
				this.selectedMediaItems = items || [];
				this.loading = false;
			},
			(err: any) => {
				this.loadError = err?.error || 'Unable to load selected media items';
				this.loading = false;
			}
		);
	}

	safe(text: any) {
		return this.sanitizer.bypassSecurityTrustHtml(text);
	}

	public getMediaItemUrl(mediaItem: MediaItem): string {
		//if (mediaItem.baseUrl) {
			//return mediaItem.baseUrl + '=w800';
		//}
		return '/api/photoframe/image?baseUrl=' + encodeURIComponent(mediaItem.baseUrl);
	}

	public formatDate(dateString: string): string {
		if (!dateString) return '';
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
		} catch {
			return dateString;
		}
	}

	public getMediaType(mimeType: string): string {
		if (!mimeType) return 'Unknown';
		if (mimeType.startsWith('image/')) return 'Photo';
		if (mimeType.startsWith('video/')) return 'Video';
		return mimeType;
	}

	public getDimensions(item: MediaItem): string {
		if (item.width && item.height) {
			return `${item.width} × ${item.height}`;
		}
		return '';
	}

	public createNewPicker(): void {
		this.selectedMediaItems = [];
		this.createPickerSession();
	}
}
